import type { RewardCreateFormValues } from '@/forms/reward-create-form/RewardCreateForm';
import { ClaimType, RewardTypeEnum } from '@/globalTypes';
import { GameOption } from '@/hooks/useGamesSearch';
import {
  getCloneDates,
  getCloneName,
  getGameDescriptorJson,
  getGameDescriptorProviderName,
  toGameProvider,
} from '@/utils/clone';
import { parseDurationToSeconds } from '@/utils/parse-duration';
import { RewardDetailQuery } from './__generated__/useRewardDetail';

export type RewardDetail = NonNullable<RewardDetailQuery['viewer']['reward']>;

const SECONDS_PER_DAY = 86400;

export const mapRewardToFormValues = (
  reward: RewardDetail,
  game?: GameOption | null,
  now = new Date(),
): Partial<RewardCreateFormValues> => {
  const { rewardType } = reward;
  const expiresInSeconds = parseDurationToSeconds(reward.expiresIn);

  const common: Partial<RewardCreateFormValues> = {
    rewardName: getCloneName(reward.name),
    claimType: reward.claimType ?? ClaimType.Instant,
    rewardSystem: reward.rewardSystem,
    campaignId: reward.campaignId,
    comment: reward.comments ?? '',
    currencyCode: rewardType.currency,
    playerIds: [],
    ...getCloneDates(reward.startsAt, reward.endsAt, now),
    ...(expiresInSeconds !== undefined && {
      expiresIn: {
        days: Math.max(1, Math.round(expiresInSeconds / SECONDS_PER_DAY)),
      },
    }),
  };

  switch (rewardType.__typename) {
    case 'FreeSpinReward': {
      const json = getGameDescriptorJson(rewardType.gameDescriptor);
      const gameProvider = toGameProvider(
        game?.providerName,
        getGameDescriptorProviderName(json),
      );

      return {
        ...common,
        rewardType: RewardTypeEnum.FreeSpinsType,
        spinsNumber: rewardType.freeSpinsAmount,
        spinsValue: rewardType.value,
        betLevel: rewardType.betLevel,
        ...(gameProvider && { gameProvider }),
        gameDetails: {
          mobileId: game?.mobileId ?? '',
          desktopId: game?.desktopId ?? '',
        },
      };
    }
    case 'MoneyDropReward':
      return {
        ...common,
        rewardType: RewardTypeEnum.MoneyDropType,
        value: rewardType.moneyDropAmount,
      };
    case 'BonusMoneyDropReward':
      return {
        ...common,
        rewardType: RewardTypeEnum.BonusMoneyDropType,
        value: rewardType.bonusMoneyDropAmount,
        maxPayout: rewardType.maxPayout,
        wageringRequirementFactor: rewardType.wageringRequirementFactor,
      };
    default:
      return common;
  }
};
